import { useEffect, useState, type FormEvent } from 'react';
import { onValue, ref as dbRef, remove, set, update } from 'firebase/database';
import { auth, db } from '@/shared/firebase/config';

// NOTA: GeoCuidado todavía NO tiene un path estable de "usuarios" en RTDB.
// Usamos `users/` como path tentativo — pendiente confirmar la ruta definitiva
// cuando se introduzca el modelo multi-tenant. Si la SaaS Manager escribe perfiles
// de admin en otra ubicación, hay que ajustar USERS_PATH aquí.
const USERS_PATH = 'users';

const MASTER_EMAILS = [
  'jllozanorodrigo@gmail.com',
  'grouplobuapp@gmail.com',
  'interlobuservicios@gmail.com',
];

type UserDoc = {
  email?: string;
  name?: string;
  role?: string;
  googleAuth?: boolean;
  createdAt?: number;
};

type UserRow = UserDoc & { id: string };

const ROLES = ['admin', 'cuidador', 'usuario'] as const;

function getTenantContext() {
  const tenantId = localStorage.getItem('tenantId') || '';
  const slug = localStorage.getItem('tenantSlug') || '';
  return { tenantId, slug };
}

function generatePassword(): string {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$';
  const arr = new Uint32Array(12);
  crypto.getRandomValues(arr);
  let out = '';
  for (let i = 0; i < arr.length; i++) out += chars[arr[i] % chars.length];
  return out;
}

export function UsersTab() {
  const [users, setUsers] = useState<UserRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [showInvite, setShowInvite] = useState(false);
  const [globalError, setGlobalError] = useState<string | null>(null);

  useEffect(() => {
    // TODO: confirmar path real de usuarios en RTDB
    console.log('[UsersTab] TODO: confirmar path real de usuarios. Usando:', USERS_PATH);
    return onValue(
      dbRef(db, USERS_PATH),
      (snap) => {
        const v = snap.val() as Record<string, UserDoc> | null;
        const list: UserRow[] = v ? Object.entries(v).map(([id, data]) => ({ id, ...(data || {}) })) : [];
        setUsers(list);
        setLoading(false);
      },
      () => setLoading(false),
    );
  }, []);

  async function changeRole(u: UserRow, role: string) {
    setGlobalError(null);
    try {
      await update(dbRef(db, `${USERS_PATH}/${u.id}`), { role });
    } catch (e) {
      setGlobalError(e instanceof Error ? e.message : 'Error cambiando rol');
    }
  }

  async function deleteUser(u: UserRow) {
    setGlobalError(null);
    if (!u.email) {
      setGlobalError('Usuario sin email; no se puede eliminar.');
      return;
    }
    if (MASTER_EMAILS.includes(u.email)) {
      setGlobalError('Los usuarios MASTER no pueden eliminarse.');
      return;
    }
    if (!confirm(`¿Eliminar al usuario ${u.email}?`)) return;
    try {
      const { tenantId, slug } = getTenantContext();
      const idToken = await auth.currentUser?.getIdToken();
      const resp = await fetch(
        'https://europe-west1-apps-en-desarrollo.cloudfunctions.net/tenantDeleteUser',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(idToken ? { Authorization: 'Bearer ' + idToken } : {}),
          },
          body: JSON.stringify({ data: { tenantId, slug, appId: 'geocuidado', email: u.email } }),
        },
      );
      if (!resp.ok) {
        const txt = await resp.text().catch(() => '');
        throw new Error(`tenantDeleteUser falló (${resp.status}) ${txt}`);
      }
      await remove(dbRef(db, `${USERS_PATH}/${u.id}`));
    } catch (e) {
      setGlobalError(e instanceof Error ? e.message : 'Error eliminando usuario');
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <h2 className="text-lg font-bold text-slate-900 flex-1">Usuarios</h2>
        <button className="btn-primary !py-1.5 !px-3 text-sm" onClick={() => setShowInvite(true)}>
          + Invitar usuario
        </button>
      </div>

      {globalError && (
        <div className="rounded-xl bg-danger-50 text-danger px-3 py-2 text-sm">{globalError}</div>
      )}

      <div className="card overflow-x-auto">
        {loading ? (
          <p className="text-sm text-slate-500">Cargando…</p>
        ) : users.length === 0 ? (
          <p className="text-sm text-slate-500">No hay usuarios todavía.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-left text-slate-500">
              <tr>
                <th className="py-2 pr-2">Email</th>
                <th className="py-2 pr-2">Nombre</th>
                <th className="py-2 pr-2">Rol</th>
                <th className="py-2 pr-2">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isMaster = !!u.email && MASTER_EMAILS.includes(u.email);
                return (
                  <tr key={u.id} className="border-t border-slate-100">
                    <td className="py-2 pr-2">
                      <span className="font-medium text-slate-800">{u.email || '—'}</span>
                      {isMaster && (
                        <span className="ml-2 inline-block bg-slate-900 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
                          MASTER
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-2 text-slate-700">{u.name || '—'}</td>
                    <td className="py-2 pr-2">
                      <select
                        className="input !py-1 !px-2 text-xs"
                        value={u.role || ''}
                        onChange={(e) => changeRole(u, e.target.value)}
                        disabled={isMaster}
                      >
                        <option value="">—</option>
                        {ROLES.map((r) => (
                          <option key={r} value={r}>{r}</option>
                        ))}
                      </select>
                    </td>
                    <td className="py-2 pr-2">
                      <button
                        className="text-danger text-xs font-semibold disabled:opacity-40"
                        onClick={() => deleteUser(u)}
                        disabled={isMaster}
                      >
                        Eliminar
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {showInvite && <InviteModal onClose={() => setShowInvite(false)} />}
    </div>
  );
}

function InviteModal({ onClose }: { onClose: () => void }) {
  const [tab, setTab] = useState<'email' | 'google'>('email');

  return (
    <div className="fixed inset-0 bg-black/40 grid place-items-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md p-5">
        <div className="flex items-center mb-4">
          <h3 className="font-bold flex-1">Invitar usuario</h3>
          <button onClick={onClose} className="text-slate-500">✕</button>
        </div>
        <div className="flex gap-1 bg-slate-100 rounded-xl p-1 mb-4">
          <button
            onClick={() => setTab('email')}
            className={`flex-1 py-2 rounded-lg text-sm font-semibold ${tab === 'email' ? 'bg-white text-brand shadow-soft' : 'text-slate-500'}`}
          >Email/Contraseña</button>
          <button
            onClick={() => setTab('google')}
            className={`flex-1 py-2 rounded-lg text-sm font-semibold ${tab === 'google' ? 'bg-white text-brand shadow-soft' : 'text-slate-500'}`}
          >Google</button>
        </div>
        {tab === 'email' ? <InviteEmailForm onDone={onClose} /> : <InviteGoogleForm onDone={onClose} />}
      </div>
    </div>
  );
}

function InviteEmailForm({ onDone }: { onDone: () => void }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<string>('cuidador');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const { tenantId, slug } = getTenantContext();
      const idToken = await auth.currentUser?.getIdToken();
      const resp = await fetch(
        'https://europe-west1-apps-en-desarrollo.cloudfunctions.net/tenantInviteUser',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(idToken ? { Authorization: 'Bearer ' + idToken } : {}),
          },
          body: JSON.stringify({
            data: { tenantId, slug, appId: 'geocuidado', email: email.trim(), password, name: name.trim(), role },
          }),
        },
      );
      if (!resp.ok) {
        const txt = await resp.text().catch(() => '');
        throw new Error(`tenantInviteUser falló (${resp.status}) ${txt}`);
      }
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error invitando usuario');
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-3">
      <div>
        <label className="label">Email</label>
        <input className="input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
      </div>
      <div>
        <label className="label">Nombre</label>
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div>
        <label className="label">Rol</label>
        <select className="input" value={role} onChange={(e) => setRole(e.target.value)}>
          {ROLES.map((r) => (<option key={r} value={r}>{r}</option>))}
        </select>
      </div>
      <div>
        <label className="label">Contraseña</label>
        <div className="flex gap-2">
          <input className="input flex-1" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} />
          <button type="button" className="btn-ghost text-xs" onClick={() => setPassword(generatePassword())}>
            Autogenerar
          </button>
        </div>
      </div>
      {error && <div className="rounded-xl bg-danger-50 text-danger px-3 py-2 text-sm">{error}</div>}
      <button type="submit" className="btn-primary w-full disabled:opacity-50" disabled={loading}>
        {loading ? 'Invitando…' : 'Invitar'}
      </button>
    </form>
  );
}

function InviteGoogleForm({ onDone }: { onDone: () => void }) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<string>('cuidador');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const id = email.trim().toLowerCase().replace(/[^a-z0-9]/g, '_');
      await set(dbRef(db, `${USERS_PATH}/${id}`), {
        email: email.trim(),
        name: name.trim(),
        role,
        googleAuth: true,
        createdAt: Date.now(),
      });
      onDone();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error');
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-3">
      <div>
        <label className="label">Email (Google)</label>
        <input className="input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} />
      </div>
      <div>
        <label className="label">Nombre</label>
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} />
      </div>
      <div>
        <label className="label">Rol</label>
        <select className="input" value={role} onChange={(e) => setRole(e.target.value)}>
          {ROLES.map((r) => (<option key={r} value={r}>{r}</option>))}
        </select>
      </div>
      <p className="text-xs text-slate-500">
        El usuario podrá iniciar sesión con su cuenta de Google. No se gestiona contraseña aquí.
      </p>
      {error && <div className="rounded-xl bg-danger-50 text-danger px-3 py-2 text-sm">{error}</div>}
      <button type="submit" className="btn-primary w-full disabled:opacity-50" disabled={loading}>
        {loading ? 'Guardando…' : 'Crear acceso Google'}
      </button>
    </form>
  );
}

export default UsersTab;
